import React, { useCallback, useEffect, useState } from 'react';
import { ActivityIndicator, Image, View } from 'react-native';
import { manipulateAsync, SaveFormat } from 'expo-image-manipulator';
import { Gesture, GestureDetector, GestureHandlerRootView } from 'react-native-gesture-handler';
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';
import { Ionicons } from '@expo/vector-icons';

import { ThemedText } from '@/components/themed-text';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { PressableScale } from '@/components/ui/pressable-scale';
import { COVER_ASPECT } from '@/constants/cover';

export { COVER_ASPECT };

/** Zoom máximo aplicável sobre o enquadramento base. */
const MAX_ZOOM = 4;
/** Largura máxima do arquivo final (px) — mantém o upload leve. */
const MAX_OUTPUT_WIDTH = 1600;

export interface PickedImage {
  uri: string;
  width: number;
  height: number;
}

interface CoverPhotoEditorProps {
  open: boolean;
  image: PickedImage | null;
  /** Enviando o resultado (trava os botões). */
  uploading?: boolean;
  onCancel: () => void;
  onConfirm: (croppedUri: string) => void;
}

/**
 * Editor de foto de capa: o cliente arrasta e dá zoom para enquadrar a imagem
 * dentro da mesma proporção que aparece na página pública. O quadro em si já é a
 * prévia "como cliente" — o que estiver visível é exatamente o que será salvo.
 *
 * O recorte é calculado em pixels da imagem original e aplicado com
 * `expo-image-manipulator`, então o arquivo enviado já vem cortado (WYSIWYG),
 * sem depender de foco/offset no render.
 */
export function CoverPhotoEditor({
  open,
  image,
  uploading = false,
  onCancel,
  onConfirm,
}: CoverPhotoEditorProps) {
  const [frameW, setFrameW] = useState(0);
  const [processing, setProcessing] = useState(false);
  // Alguns pickers (ex.: web) não trazem width/height — medimos como fallback.
  const [measured, setMeasured] = useState<{ w: number; h: number } | null>(null);

  const translateX = useSharedValue(0);
  const translateY = useSharedValue(0);
  const scale = useSharedValue(1);
  const startX = useSharedValue(0);
  const startY = useSharedValue(0);
  const startScale = useSharedValue(1);

  const frameH = frameW / COVER_ASPECT;
  const iw = image && image.width > 0 ? image.width : (measured?.w ?? 1);
  const ih = image && image.height > 0 ? image.height : (measured?.h ?? 1);
  // Escala mínima que faz a imagem cobrir todo o quadro (resizeMode cover).
  const baseScale = frameW > 0 ? Math.max(frameW / iw, frameH / ih) : 1;

  // Reseta o enquadramento sempre que troca a imagem.
  useEffect(() => {
    translateX.value = 0;
    translateY.value = 0;
    scale.value = 1;
    setMeasured(null);
    if (image && (image.width <= 0 || image.height <= 0)) {
      Image.getSize(
        image.uri,
        (w, h) => setMeasured({ w, h }),
        () => undefined,
      );
    }
  }, [image?.uri, image, scale, translateX, translateY]);

  // Limites de deslocamento para o quadro nunca "vazar" (mostrar borda).
  const clampTranslate = useCallback(
    (s: number) => {
      'worklet';
      const dw = iw * baseScale * s;
      const dh = ih * baseScale * s;
      const maxX = Math.max(0, (dw - frameW) / 2);
      const maxY = Math.max(0, (dh - frameH) / 2);
      translateX.value = Math.min(maxX, Math.max(-maxX, translateX.value));
      translateY.value = Math.min(maxY, Math.max(-maxY, translateY.value));
    },
    [iw, ih, baseScale, frameW, frameH, translateX, translateY],
  );

  const panGesture = Gesture.Pan()
    .onStart(() => {
      startX.value = translateX.value;
      startY.value = translateY.value;
    })
    .onUpdate((e) => {
      translateX.value = startX.value + e.translationX;
      translateY.value = startY.value + e.translationY;
      clampTranslate(scale.value);
    });

  const pinchGesture = Gesture.Pinch()
    .onStart(() => {
      startScale.value = scale.value;
    })
    .onUpdate((e) => {
      scale.value = Math.min(MAX_ZOOM, Math.max(1, startScale.value * e.scale));
      clampTranslate(scale.value);
    });

  const composed = Gesture.Simultaneous(panGesture, pinchGesture);

  const animatedStyle = useAnimatedStyle(() => ({
    width: iw * baseScale * scale.value,
    height: ih * baseScale * scale.value,
    transform: [{ translateX: translateX.value }, { translateY: translateY.value }],
  }));

  const stepZoom = (delta: number) => {
    const next = Math.min(MAX_ZOOM, Math.max(1, scale.value + delta));
    scale.value = withTiming(next, { duration: 160 });
    clampTranslate(next);
  };

  const handleConfirm = async () => {
    if (!image || frameW <= 0) return;
    setProcessing(true);
    try {
      const f = baseScale * scale.value;
      const cropW = frameW / f;
      const cropH = frameH / f;
      let cropX = iw / 2 - cropW / 2 - translateX.value / f;
      let cropY = ih / 2 - cropH / 2 - translateY.value / f;
      cropX = Math.max(0, Math.min(iw - cropW, cropX));
      cropY = Math.max(0, Math.min(ih - cropH, cropY));

      const crop = {
        originX: Math.round(cropX),
        originY: Math.round(cropY),
        width: Math.max(1, Math.round(cropW)),
        height: Math.max(1, Math.round(cropH)),
      };

      const actions: Parameters<typeof manipulateAsync>[1] = [{ crop }];
      if (crop.width > MAX_OUTPUT_WIDTH) {
        actions.push({ resize: { width: MAX_OUTPUT_WIDTH } });
      }

      const result = await manipulateAsync(image.uri, actions, {
        compress: 0.85,
        format: SaveFormat.JPEG,
      });
      onConfirm(result.uri);
    } finally {
      setProcessing(false);
    }
  };

  const busy = processing || uploading;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !busy && onCancel()} dismissable={!busy}>
      <DialogContent className="gap-4 p-4">
        <View className="gap-1">
          <ThemedText className="font-bold text-lg">Ajustar foto de capa</ThemedText>
          <ThemedText className="text-foreground/60 text-sm">
            Arraste e use o zoom para enquadrar. É assim que o cliente vai ver.
          </ThemedText>
        </View>

        {/* Quadro = prévia como cliente */}
        <GestureHandlerRootView>
          <View
            onLayout={(e) => setFrameW(e.nativeEvent.layout.width)}
            className="items-center justify-center overflow-hidden rounded-2xl bg-neutral-200"
            style={{ width: '100%', aspectRatio: COVER_ASPECT, borderCurve: 'continuous' }}
          >
            {image && frameW > 0 && (
              <GestureDetector gesture={composed}>
                <Animated.View
                  style={[{ alignItems: 'center', justifyContent: 'center' }, animatedStyle]}
                >
                  <Image
                    source={{ uri: image.uri }}
                    accessibilityLabel="Prévia da foto de capa"
                    style={{ width: '100%', height: '100%' }}
                    resizeMode="cover"
                  />
                </Animated.View>
              </GestureDetector>
            )}

            {/* Guias sutis de enquadramento */}
            <View pointerEvents="none" className="absolute inset-0 items-center justify-center">
              <Ionicons name="move-outline" size={26} color="rgba(255,255,255,0.7)" />
            </View>
          </View>
        </GestureHandlerRootView>

        {/* Controle de zoom (funciona em qualquer plataforma, inclusive web) */}
        <View className="flex-row items-center justify-center gap-4">
          <PressableScale
            onPress={() => stepZoom(-0.3)}
            disabled={busy}
            className="h-11 w-11 items-center justify-center rounded-full bg-card"
            accessibilityLabel="Diminuir zoom"
          >
            <Ionicons className="text-neutral-800" name="remove" size={22} />
          </PressableScale>
          <Ionicons className="text-neutral-400" name="search-outline" size={18} />
          <PressableScale
            onPress={() => stepZoom(0.3)}
            disabled={busy}
            className="h-11 w-11 items-center justify-center rounded-full bg-card"
            accessibilityLabel="Aumentar zoom"
          >
            <Ionicons className="text-neutral-800" name="add" size={22} />
          </PressableScale>
        </View>

        <View className="flex-row gap-2">
          <Button
            variant="outline"
            className="flex-1"
            title="Cancelar"
            disabled={busy}
            onPress={onCancel}
          />
          <Button
            className="flex-1"
            title={busy ? 'Salvando...' : 'Usar foto'}
            disabled={busy || frameW <= 0}
            onPress={() => void handleConfirm()}
          />
        </View>

        {busy && (
          <View className="flex-row items-center justify-center gap-2">
            <ActivityIndicator size="small" />
            <ThemedText className="text-foreground/50 text-xs">
              {processing ? 'Cortando imagem...' : 'Enviando...'}
            </ThemedText>
          </View>
        )}
      </DialogContent>
    </Dialog>
  );
}
